import { useContext, useEffect, useRef } from "react";
import { WorkspaceWindowFocusContext } from "./workspace-local-state";

/**
 * A keyboard shortcut that belongs to one window.
 *
 * Every open app keeps its tree mounted, minimized ones included, so a listener bound straight to
 * the window object acts for a view the person cannot see: a background editor would save to disk
 * on a Save pressed while working somewhere else. Outside the windowed workspace there is one
 * view, which is always the foreground, so standalone screens behave as before.
 */
export function useWindowShortcut(handler: (event: KeyboardEvent) => void, enabled = true) {
  const focused = useContext(WorkspaceWindowFocusContext);
  const current = useRef(handler);
  current.current = handler;
  useEffect(() => {
    if (!focused || !enabled) return;
    const key = (event: KeyboardEvent) => current.current(event);
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [focused, enabled]);
}
